import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, TrendingDown, RefreshCw, ChevronRight, Lock } from "lucide-react";

function probColor(p) {
  if (p >= 75) return "text-[#22C55E] border-[#22C55E]/40";
  if (p >= 65) return "text-[#F59E0B] border-[#F59E0B]/40";
  return "text-zinc-300 border-zinc-700";
}

export default function TopSetups({ segment, onPick }) {
  const { user } = useAuth();
  const isPaid = user && (user.is_admin || user.plan === "pro");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isPaid) return; // do not even fetch for free users
    setLoading(true);
    try {
      const { data } = await api.get(`/watchlist/top-setups?limit=10${segment ? `&segment=${segment}` : ""}`);
      setItems(data || []);
    } catch (e) {
      console.warn("top-setups load failed:", e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [isPaid, segment]);

  useEffect(() => {
    setItems([]);
    load();
    // Refresh on the same 2-min cadence as the Trade Setup panel so both views
    // stay in sync on the SHARED signal cache (a stale strip previously kept
    // advertising a setup the panel had already re-scored below threshold).
    const id = setInterval(load, 120000);
    return () => clearInterval(id);
  }, [load]);

  // Free users get a locked teaser only — no symbols, no levels, no rankings.
  if (!isPaid) {
    return (
      <section className="border-b border-zinc-900 px-4 md:px-6 py-3 bg-[#0e0e0e]" data-testid="top-setups-locked">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF] font-mono flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Top setups now
            </div>
            <div className="text-xs text-zinc-500">Top-5 AI-ranked trades across your watchlist · unlock with Pro</div>
          </div>
          <Link
            to="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 bg-[#007AFF] hover:bg-[#3395FF] text-white"
            data-testid="top-setups-upgrade"
          >
            Unlock Top setups
          </Link>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 select-none">
          {[1,2,3,4,5].map((n) => (
            <div key={n} className="bg-[#121212] border border-zinc-800 p-3 relative overflow-hidden" data-testid={`top-setup-locked-${n}`}>
              <div className="absolute inset-0 backdrop-blur-md bg-black/40 z-10 flex items-center justify-center">
                <Lock className="h-4 w-4 text-zinc-600" />
              </div>
              <div className="font-mono text-xs text-zinc-700 uppercase tracking-widest">#{n} · ████</div>
              <div className="font-heading font-bold uppercase tracking-tight text-base text-zinc-800">████</div>
              <div className="mt-2 font-mono text-xs text-zinc-800">ENTRY ██,███</div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-800">SL ██,███ · T1 ██,███</div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="border-b border-zinc-900 px-4 md:px-6 py-3 bg-[#0e0e0e]" data-testid="top-setups">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#007AFF] font-mono">◢ Top setups now</div>
          <div className="text-xs text-zinc-500">Ranked by AI probability · {segment === "global" ? "Global markets" : "Indian markets"} · 5m intraday</div>
        </div>
        {user?.is_admin && (
          <button onClick={load} disabled={loading} className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest flex items-center gap-1" data-testid="top-setups-refresh">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        )}
      </div>

      {loading && items.length === 0 && (
        <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 py-4" data-testid="top-setups-loading">
          ◢ Scanning watchlist · this takes ~20s on first run (cached after)
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-xs text-zinc-500 py-2">No live setups in {segment === "global" ? "Global" : "Indian"} markets yet — add symbols via ⌘K.</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
        {items.map((s, idx) => (
          <button
            key={s.symbol}
            onClick={() => onPick && onPick(s)}
            className="text-left bg-[#121212] hover:bg-[#1a1a1a] border border-zinc-800 hover:border-[#007AFF]/60 p-3 transition-colors group relative"
            data-testid={`top-setup-${idx}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-xs text-zinc-500 uppercase tracking-widest">#{idx + 1} · {s.category}</div>
                <div className="font-heading font-bold uppercase tracking-tight text-base truncate" data-testid={`top-setup-symbol-${idx}`}>
                  {s.symbol}
                </div>
              </div>
              <div className={`px-2 py-0.5 border text-xs font-mono font-bold ${probColor(s.success_probability)}`} data-testid={`top-setup-prob-${idx}`}>
                {s.success_probability}%
              </div>
            </div>

            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className={`inline-flex items-center gap-1 font-bold uppercase ${s.trend === "bullish" ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                {s.trend === "bullish" ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                {s.trend}
              </span>
              <span className="text-zinc-700">·</span>
              <span className="font-mono text-zinc-400">R:R {s.risk_reward}</span>
              {s.data_quality === "live" && (
                <span className="ml-auto inline-flex items-center gap-1 text-[#22C55E] text-[10px] uppercase tracking-widest"><span className="h-1.5 w-1.5 bg-[#22C55E] rounded-full" /> Live</span>
              )}
            </div>

            <div className="mt-2 font-mono text-xs text-zinc-300">
              ENTRY <span className="text-white font-bold">{Number(s.entry_price).toLocaleString("en-IN", { maximumFractionDigits: 4 })}</span>
            </div>
            <div className="mt-0.5 grid grid-cols-2 gap-x-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <span>SL <span className="text-[#EF4444]">{Number(s.stop_loss).toLocaleString("en-IN", { maximumFractionDigits: 4 })}</span></span>
              <span>T1 <span className="text-[#22C55E]">{Number(s.target_1).toLocaleString("en-IN", { maximumFractionDigits: 4 })}</span></span>
            </div>

            {s.wave_count && user?.is_admin && (
              <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-600 truncate" title={s.wave_count}>
                {s.wave_count}
              </div>
            )}

            <ChevronRight className="absolute right-2 bottom-2 h-3 w-3 text-zinc-700 group-hover:text-[#007AFF]" />
          </button>
        ))}
      </div>
    </section>
  );
}
